import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Back navigation that POPS the history stack (returns to wherever you came
 * from — e.g. the campaign detail after "View delivery logs", not always the
 * list), falling back to `fallback` ONLY on a cold / deep-link entry with no
 * in-app history to pop.
 *
 * Router-agnostic on purpose: react-router stamps the FIRST location's `key`
 * as `"default"` for BOTH the website's browser-history router AND the
 * extension's in-memory router (which has no `window.history`). So
 * `key === 'default'` is the portable "we're at the entry point" signal —
 * unlike `window.history.state.idx`, which exists only for the browser router
 * and is always undefined under MemoryRouter.
 */
export function useRouterBack(fallback: string): () => void {
  const navigate = useNavigate();
  const { key } = useLocation();
  return useCallback(() => {
    if (key !== 'default') navigate(-1);
    else navigate(fallback);
  }, [key, navigate, fallback]);
}
